import React, { useContext } from 'react'
import {
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useNavigate } from 'react-router-dom'
import AmbuNetLogo from '../../../assets/images/ambunetLogo.png'
import { TenantSettingsContext } from '../../../App'
import PoweredBySmall from '../../../components/Corporate/PoweredBySmall'

const accountTypes = [
  {
    title: 'Employee / Applicant',
    subtitle: 'Join the team and manage your application.',
    color: '#00964C',
    glow: 'rgba(0,150,76,0.28)',
    icon: <PersonAddAlt1Icon />,
    accountType: 'applicant',
  },
  {
    title: 'Event Organiser',
    subtitle: 'Manage your event medical cover.',
    color: '#E32525',
    glow: 'rgba(227,37,37,0.25)',
    icon: <EventAvailableIcon />,
    accountType: 'event',
  },
  {
    title: 'Patient Transport',
    subtitle: 'Manage your patient transport bookings.',
    caption: 'Hospitals should contact the organisation directly.',
    color: '#85276A',
    glow: 'rgba(133,39,106,0.28)',
    icon: <LocalTaxiIcon />,
    accountType: 'transport',
  },
]

const RegisterSelector = () => {
  const navigate = useNavigate()
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      padding={2}
      sx={{
        background:
          'radial-gradient(circle at top left, rgba(35,154,164,0.25), transparent 35%), linear-gradient(135deg, #1A2230 0%, #242F40 45%, #101827 100%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 820,
          borderRadius: 5,
          overflow: 'hidden',
          background: 'rgba(255,255,255,0.96)',
          border: '1px solid rgba(255,255,255,0.16)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
        }}
      >
        <Box
          sx={{
            background:
              'linear-gradient(135deg, #242F40 0%, #2c3e50 60%, #239AA4 160%)',
            color: 'white',
            padding: { xs: 3, sm: 4 },
          }}
        >
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box
              component="img"
              src={orgSettings?.logo || AmbuNetLogo}
              alt="AmbuNet"
              sx={{
                maxHeight: 64,
                maxWidth: 180,
                objectFit: 'contain',
                backgroundColor: 'white',
                borderRadius: 2,
                padding: 1,
              }}
            />

            <Stack spacing={0.75}>
              <Typography variant="overline" sx={{ opacity: 0.75 }}>
                {orgSettings?.orgName ?? 'Account Registration'}
              </Typography>

              <Typography variant="h4" fontWeight={800} color="whitesmoke">
                Choose your account type
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.78)',
                  maxWidth: 520,
                }}
              >
                Select the account type that best matches how you’ll be using
                AmbuNet.
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box padding={{ xs: 2.5, sm: 4 }}>
          <Stack spacing={2}>
            {accountTypes.map((item) => (
              <Button
                key={item.title}
                fullWidth
                onClick={() => navigate(`?accountType=${item.accountType}`)}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  borderRadius: 4,
                  padding: 2,
                  minHeight: 112,
                  backgroundColor: '#FFFFFF',
                  color: '#242F40',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 8px 24px rgba(36,47,64,0.08)',
                  textTransform: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.22s ease',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: `radial-gradient(circle at left, ${item.glow}, transparent 42%)`,
                    opacity: 0,
                    transition: 'opacity 0.22s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    borderColor: item.color,
                    boxShadow: `0 14px 34px ${item.glow}`,
                    backgroundColor: '#FFFFFF',
                  },
                  '&:hover:before': {
                    opacity: 1,
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  width="100%"
                  position="relative"
                  zIndex={1}
                >
                  <Box
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: item.color,
                      color: 'white',
                      flexShrink: 0,
                      boxShadow: `0 10px 24px ${item.glow}`,
                      '& svg': {
                        fontSize: 30,
                      },
                    }}
                  >
                    {item.icon}
                  </Box>

                  <Stack spacing={0.5} flex={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        color="secondary"
                      >
                        {item.title}
                      </Typography>
                    </Stack>

                    <Typography variant="body2" color="text.secondary">
                      {item.subtitle}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {item.caption}
                    </Typography>
                  </Stack>

                  <ArrowForwardIcon
                    sx={{
                      color: item.color,
                      fontSize: 28,
                      display: { xs: 'none', sm: 'block' },
                    }}
                  />
                </Stack>
              </Button>
            ))}
          </Stack>
          <Stack spacing={1} marginTop={4}>
            <Typography
              variant="caption"
              display="block"
              textAlign="center"
              color="text.secondary"
            >
              Not sure which account you need? Please contact{' '}
              <Link
                href={
                  orgSettings?.replyToEmail &&
                  `mailto:${orgSettings.replyToEmail}`
                }
                target="_blank"
              >
                {orgSettings?.orgName}
              </Link>{' '}
              directly.
            </Typography>

            <PoweredBySmall />
          </Stack>
        </Box>
      </Paper>
    </Box>
  )
}

export default RegisterSelector
